import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, Pressable, View } from 'react-native';
// On phone, render the FlatList through gesture-handler's ScrollView so it negotiates with the
// sheet's drag-to-close Pan (same pattern the add-player form uses) — the list scrolls AND drags.
import { ScrollView as GestureScrollView } from 'react-native-gesture-handler';
import { useKeyboardState } from 'react-native-keyboard-controller';

import { Stack } from '@/components/layout';
import { SheetContent, SheetModal, Text, TextInput } from '@/components/ui';
import Modal from '@/components/ui/modal/modal';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

export type SearchableSelectItem = {
  value: string;
  label: string;
  /** Optional right-aligned secondary text (e.g. a dial code). */
  meta?: string;
};

type Section = { title: string; data: SearchableSelectItem[] };
type ListRow =
  | { type: 'header'; key: string; title: string }
  | { type: 'item'; key: string; item: SearchableSelectItem };
const EMPTY_FEATURED_VALUES: string[] = [];

export type SearchableSelectSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  searchPlaceholder: string;
  items: SearchableSelectItem[];
  selectedValue?: string | null;
  onSelect: (value: string) => void;
  /** Group options into named sections. */
  grouped?: boolean;
  /** Message shown when the (filtered) list is empty. */
  emptyText: string;
  /** Values rendered before the grouped A-Z sections (for example, US-first country lists). */
  featuredValues?: string[];
  /** Section title used for featured values. */
  featuredSectionTitle?: string;
};

/**
 * Searchable bottom-sheet list used by the address Country/Region and State/Province
 * pickers. Supports a live search filter, optional featured values, A-Z grouping,
 * a right-aligned meta column (dial codes), and an empty state. Purely presentational —
 * the caller owns the value.
 */
export function SearchableSelectSheet({
  open,
  onOpenChange,
  title,
  searchPlaceholder,
  items,
  selectedValue,
  onSelect,
  grouped = false,
  emptyText,
  featuredValues = EMPTY_FEATURED_VALUES,
  featuredSectionTitle = '',
}: SearchableSelectSheetProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const [query, setQuery] = useState('');
  const listRef = useRef<FlatList<ListRow>>(null);
  const keyboardHeight = useKeyboardState(state => (state.isVisible ? state.height : 0));
  const itemRowHeight = scale(48);
  const headerRowHeight = scale(42);
  const listContentStyle = useMemo(
    () => [styles.listContent, keyboardHeight > 0 && { paddingBottom: keyboardHeight + scale(24) }],
    [keyboardHeight, scale, styles.listContent],
  );

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      normalizedQuery
        ? items.filter(
            item =>
              item.label.toLowerCase().includes(normalizedQuery) ||
              // Also match the dial code (item.meta, e.g. "+93"), with or without the leading "+".
              Boolean(item.meta?.toLowerCase().includes(normalizedQuery)),
          )
        : items,
    [items, normalizedQuery],
  );

  const sections = useMemo<Section[]>(() => {
    if (!grouped) {
      // State combobox: a single untitled section (no headers, no "Selected").
      return filtered.length > 0 ? [{ title: '', data: filtered }] : [];
    }

    const result: Section[] = [];
    const featuredItems = featuredValues
      .map(featuredValue => filtered.find(item => item.value === featuredValue))
      .filter((item): item is SearchableSelectItem => Boolean(item));
    if (featuredItems.length > 0) {
      result.push({ title: featuredSectionTitle, data: featuredItems });
    }

    const groups = new Map<string, SearchableSelectItem[]>();
    for (const item of filtered) {
      if (featuredValues.includes(item.value)) continue;
      const letter = (item.label.charAt(0) || '#').toUpperCase();
      const bucket = groups.get(letter);
      if (bucket) bucket.push(item);
      else groups.set(letter, [item]);
    }
    for (const letter of [...groups.keys()].sort()) {
      result.push({ title: letter, data: groups.get(letter) ?? [] });
    }
    return result;
  }, [featuredSectionTitle, featuredValues, filtered, grouped]);

  const listRows = useMemo<ListRow[]>(
    () =>
      sections.flatMap(section => [
        ...(section.title
          ? [{ type: 'header' as const, key: `header-${section.title}`, title: section.title }]
          : []),
        ...section.data.map(item => ({ type: 'item' as const, key: item.value, item })),
      ]),
    [sections],
  );

  const rowLayouts = useMemo(() => {
    let offset = 0;
    return listRows.map((row, index) => {
      const length = row.type === 'header' ? headerRowHeight : itemRowHeight;
      const layout = { index, length, offset };
      offset += length;
      return layout;
    });
  }, [headerRowHeight, itemRowHeight, listRows]);

  const selectedIndex = useMemo(
    () =>
      selectedValue
        ? listRows.findIndex(row => row.type === 'item' && row.item.value === selectedValue)
        : -1,
    [listRows, selectedValue],
  );

  const scrollToSelected = useCallback(() => {
    if (!open || selectedIndex < 0) return;
    listRef.current?.scrollToIndex({
      index: selectedIndex,
      animated: true,
      viewPosition: 0,
    });
  }, [open, selectedIndex]);

  useEffect(() => {
    if (!open) return;

    // Let the modal and the first virtualized batch render before scrolling.
    const timeout = setTimeout(scrollToSelected, 250);
    return () => clearTimeout(timeout);
  }, [open, scrollToSelected]);

  useEffect(() => {
    if (!open) return;

    // Filtering changes the data but FlatList preserves its previous offset. Reset after the
    // filtered rows commit so the first matching result is immediately visible below search.
    const frame = requestAnimationFrame(() => {
      listRef.current?.scrollToOffset({ offset: 0, animated: false });
    });
    return () => cancelAnimationFrame(frame);
  }, [normalizedQuery, open]);

  const handleSelect = (value: string) => {
    onSelect(value);
    setQuery('');
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) setQuery('');
    onOpenChange(next);
  };

  // Inner content shared by both layouts (phone bottom sheet + tablet centered card).
  const body = (
    <>
      {/* Centered title. */}
      <Stack align="center">
        <Text
          variant="Font Guardian App/Body/Body 16 Semibold"
          numberOfLines={1}
          style={styles.title}>
          {title}
        </Text>
      </Stack>

      <TextInput value={query} onChangeText={setQuery} status="default">
        <TextInput.Content style={styles.searchContent}>
          <TextInput.Icon
            position="left"
            name="IcSearch1"
            size={scale(20)}
            color={colors['6']}
            styleWrapper={styles.iconSearch}
          />
          <TextInput.Field
            placeholder={searchPlaceholder}
            autoCapitalize="none"
            variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
            autoCorrect={false}
          />
        </TextInput.Content>
      </TextInput>

      {listRows.length === 0 ? (
        <Stack align="center" justify="center" style={styles.emptyWrap}>
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.emptyText}>
            {emptyText}
          </Text>
        </Stack>
      ) : (
        <FlatList
          ref={listRef}
          data={listRows}
          keyExtractor={row => row.key}
          getItemLayout={(_, index) =>
            rowLayouts[index] ?? { index, length: itemRowHeight, offset: index * itemRowHeight }
          }
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          style={styles.list}
          contentContainerStyle={listContentStyle}
          // gesture-handler ScrollView is only needed for the phone sheet's drag-to-close; the
          // tablet card is a plain modal, so let FlatList use its default ScrollView there.
          renderScrollComponent={
            isTablet ? undefined : scrollProps => <GestureScrollView {...scrollProps} />
          }
          renderItem={({ item: row }) => {
            if (row.type === 'header') {
              return (
                <Text
                  variant="Font Guardian App/Heading/Heading H6 18px"
                  style={[
                    styles.sectionHeader,
                    { height: headerRowHeight, lineHeight: headerRowHeight },
                  ]}>
                  {row.title}
                </Text>
              );
            }

            const { item } = row;
            const isSelected = item.value === selectedValue;
            return (
              <Pressable
                accessibilityRole="button"
                onPress={() => handleSelect(item.value)}
                style={[
                  styles.row,
                  { height: itemRowHeight },
                  isSelected ? styles.rowSelected : undefined,
                ]}>
                <Text
                  variant={
                    isSelected
                      ? 'Font Guardian App/Body/Body 16 Semibold'
                      : 'Font Guardian App/Subtitle/Subtitle 14 Regular'
                  }
                  numberOfLines={1}
                  style={styles.rowLabel}>
                  {item.label}
                </Text>
                {item.meta ? (
                  <Text
                    variant={
                      isSelected
                        ? 'Font Guardian App/Subtitle/Subtitle 14 Semibold'
                        : 'Font Guardian App/Subtitle/Subtitle 14 Regular'
                    }
                    style={styles.rowMeta}>
                    {item.meta}
                  </Text>
                ) : null}
              </Pressable>
            );
          }}
        />
      )}
    </>
  );

  // Tablet: a centered popup card (matches the Add player tablet popup) instead of a side sheet.
  if (isTablet) {
    return (
      <Modal
        visible={open}
        onBackdropPress={() => handleOpenChange(false)}
        onBackButtonPress={() => handleOpenChange(false)}>
        <View style={styles.card}>{body}</View>
      </Modal>
    );
  }

  // Phone: a bottom sheet with a grab handle, dismissed by dragging down or tapping the backdrop.
  return (
    <SheetModal open={open} onOpenChange={handleOpenChange}>
      <SheetContent closeOnBackdropPress style={styles.sheet}>
        <Stack style={styles.handle} />
        {body}
      </SheetContent>
    </SheetModal>
  );
}

export default SearchableSelectSheet;

const useStyles = createStyles(({ scale, deviceWidth, deviceHeight }) => ({
  // Sheet container per design: 16px top corners, 24/16 padding, 8px gap, white background.
  sheet: {
    height: '85%',
    backgroundColor: colors['0'],
    borderTopLeftRadius: scale(16),
    borderTopRightRadius: scale(16),
    paddingTop: scale(24),
    paddingHorizontal: scale(16),
    gap: scale(8),
  },
  // Tablet: centered popup card (mirrors the Add player tablet popup sizing).
  card: {
    width: Math.min(scale(512), deviceWidth * 0.9),
    height: Math.min(scale(700), deviceHeight * 0.85),
    backgroundColor: colors['0'],
    borderRadius: scale(16),
    paddingTop: scale(24),
    paddingHorizontal: scale(16),
    gap: scale(8),
    overflow: 'hidden',
  },
  handle: {
    width: scale(48),
    height: scale(4),
    borderRadius: scale(6),
    backgroundColor: colors['Color 10'],
    alignSelf: 'center',
  },
  title: {
    textAlign: 'center',
    color: guardianColors['10'],
  },
  // Search box per design: 8/12 padding, 8px gap, 8px radius, 1px #E2E8F0 border, white fill.
  searchContent: {
    backgroundColor: colors['0'],
    borderColor: guardianColors['3'],
    borderWidth: 1,
    borderRadius: scale(8),
    paddingVertical: scale(8),
    paddingHorizontal: scale(12),
    gap: scale(8),
  },
  iconSearch: {
    marginRight: 0,
  },
  // The container owns the horizontal padding; the list only adds bottom breathing room.
  list: {
    flex: 1,
  },
  listContent: {
    paddingBottom: scale(24),
  },
  sectionHeader: {
    color: guardianColors['10'],
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: scale(8),
    paddingRight: scale(16),
    paddingLeft: scale(24),
    gap: scale(19),
  },
  rowSelected: {
    backgroundColor: guardianColors['1'],
  },
  rowLabel: {
    flex: 1,
    color: guardianColors['10'],
  },
  rowMeta: {
    color: guardianColors['6'],
  },
  emptyWrap: {
    paddingVertical: scale(48),
  },
  emptyText: {
    color: guardianColors['6'],
  },
}));
